// ClinicPage.jsx - Redesigned Clinic Detail Page
"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Star,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import ChatBot from "./ChatBot";
import { API_ENDPOINTS, DEFAULT_FETCH_CONFIG } from "../config/api.config";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

// Platform contact information
const PLATFORM_CONTACT = {
  phone: "+1 (780) 267-7551",
  email: "info@bookatherapy.com",
};

// 3D Array of Reviews - Each sub-array contains 10-15 reviews
const REVIEWS_3D = [
  // Sub-array 1
  [
    {
      name: "Sarah M.",
      rating: 5,
      text: "Excellent service! The therapists are professional and the atmosphere is relaxing.",
    },
    {
      name: "Michael T.",
      rating: 5,
      text: "Best massage therapy experience I've had. Highly recommend!",
    },
    {
      name: "Emily R.",
      rating: 5,
      text: "Very clean facility and friendly staff. Will definitely return.",
    },
    {
      name: "David L.",
      rating: 4,
      text: "Great service, though the waiting area could be larger.",
    },
    {
      name: "Jessica K.",
      rating: 5,
      text: "Amazing therapists who really understand your needs.",
    },
    {
      name: "Robert P.",
      rating: 5,
      text: "Professional service from start to finish. Very satisfied.",
    },
    {
      name: "Amanda S.",
      rating: 4,
      text: "Good experience overall. The booking process was easy.",
    },
    {
      name: "James W.",
      rating: 5,
      text: "Outstanding massage therapy. Helped with my back pain significantly.",
    },
    {
      name: "Lisa H.",
      rating: 5,
      text: "Clean, professional, and effective. Exactly what I needed.",
    },
    {
      name: "Thomas B.",
      rating: 4,
      text: "Very good service. The staff is knowledgeable and friendly.",
    },
    {
      name: "Jennifer D.",
      rating: 5,
      text: "Top-notch service! I've been coming here for months.",
    },
    {
      name: "Christopher N.",
      rating: 5,
      text: "Excellent therapists and a welcoming environment.",
    },
  ],
  // Sub-array 2
  [
    {
      name: "Patricia F.",
      rating: 5,
      text: "Wonderful experience! The massage was exactly what I needed.",
    },
    {
      name: "Daniel G.",
      rating: 4,
      text: "Good service, professional staff. Would recommend.",
    },
    {
      name: "Michelle Y.",
      rating: 5,
      text: "Best massage therapy clinic in the area. Very impressed!",
    },
    {
      name: "Mark J.",
      rating: 5,
      text: "Professional and courteous staff. Great atmosphere.",
    },
    {
      name: "Nancy C.",
      rating: 4,
      text: "Nice facility, good service. Happy with my visit.",
    },
    {
      name: "Paul V.",
      rating: 5,
      text: "Excellent therapists who really care about your well-being.",
    },
    {
      name: "Laura Z.",
      rating: 5,
      text: "Amazing service! Helped relieve my chronic pain.",
    },
    {
      name: "Kevin X.",
      rating: 4,
      text: "Very good experience. The booking was straightforward.",
    },
    {
      name: "Susan Q.",
      rating: 5,
      text: "Professional, clean, and effective. Highly recommend!",
    },
    {
      name: "Brian E.",
      rating: 5,
      text: "Outstanding service from knowledgeable therapists.",
    },
    {
      name: "Karen I.",
      rating: 4,
      text: "Good massage therapy. The facility is well-maintained.",
    },
    {
      name: "Steven O.",
      rating: 5,
      text: "Excellent experience! Will definitely come back.",
    },
    {
      name: "Betty U.",
      rating: 5,
      text: "Best massage I've ever had. The staff is wonderful.",
    },
  ],
  // Sub-array 3
  [
    {
      name: "Edward A.",
      rating: 5,
      text: "Professional service and great results. Very satisfied!",
    },
    {
      name: "Helen M.",
      rating: 4,
      text: "Good experience overall. The therapists are skilled.",
    },
    {
      name: "Ronald T.",
      rating: 5,
      text: "Excellent clinic with knowledgeable staff.",
    },
    {
      name: "Sharon R.",
      rating: 5,
      text: "Amazing massage therapy! Helped with my stress levels.",
    },
    {
      name: "Anthony L.",
      rating: 4,
      text: "Very good service. Clean and professional environment.",
    },
    {
      name: "Cynthia K.",
      rating: 5,
      text: "Outstanding therapists who understand your needs.",
    },
    {
      name: "Kenneth P.",
      rating: 5,
      text: "Best massage therapy experience. Highly recommend!",
    },
    {
      name: "Angela S.",
      rating: 4,
      text: "Good service, friendly staff. Will return.",
    },
    {
      name: "Joshua W.",
      rating: 5,
      text: "Excellent facility with professional therapists.",
    },
    {
      name: "Brenda H.",
      rating: 5,
      text: "Wonderful experience! The booking process was easy.",
    },
    {
      name: "Ryan B.",
      rating: 4,
      text: "Very good massage therapy. Clean and comfortable.",
    },
    {
      name: "Pamela D.",
      rating: 5,
      text: "Professional service from start to finish.",
    },
    {
      name: "Gary N.",
      rating: 5,
      text: "Amazing therapists and a relaxing atmosphere.",
    },
    {
      name: "Nicole F.",
      rating: 4,
      text: "Good experience. The staff is knowledgeable.",
    },
  ],
  // Sub-array 4
  [
    {
      name: "Larry G.",
      rating: 5,
      text: "Excellent massage therapy! Very professional service.",
    },
    {
      name: "Frances Y.",
      rating: 5,
      text: "Best clinic in town. The therapists are amazing.",
    },
    {
      name: "Wayne J.",
      rating: 4,
      text: "Good service overall. Clean and well-maintained.",
    },
    {
      name: "Christine C.",
      rating: 5,
      text: "Outstanding experience! Helped with my muscle tension.",
    },
    {
      name: "Ralph V.",
      rating: 5,
      text: "Professional staff and excellent massage therapy.",
    },
    {
      name: "Samantha Z.",
      rating: 4,
      text: "Very good service. The booking was easy.",
    },
    {
      name: "Albert X.",
      rating: 5,
      text: "Amazing therapists who really care about your health.",
    },
    {
      name: "Marie Q.",
      rating: 5,
      text: "Excellent facility with knowledgeable staff.",
    },
    {
      name: "Eugene E.",
      rating: 4,
      text: "Good massage therapy. Clean and comfortable.",
    },
    {
      name: "Janet I.",
      rating: 5,
      text: "Best experience! Will definitely come back.",
    },
    {
      name: "Louis O.",
      rating: 5,
      text: "Professional service from knowledgeable therapists.",
    },
    {
      name: "Catherine U.",
      rating: 4,
      text: "Very good service. The staff is friendly.",
    },
    {
      name: "Russell A.",
      rating: 5,
      text: "Excellent massage therapy! Highly recommend!",
    },
  ],
  // Sub-array 5
  [
    {
      name: "Joan M.",
      rating: 5,
      text: "Wonderful experience! The therapists are professional.",
    },
    {
      name: "John T.",
      rating: 4,
      text: "Good service, clean facility. Would recommend.",
    },
    {
      name: "Evelyn R.",
      rating: 5,
      text: "Amazing massage therapy! Helped with my back pain.",
    },
    {
      name: "Raymond L.",
      rating: 5,
      text: "Outstanding service from skilled therapists.",
    },
    {
      name: "Joyce K.",
      rating: 4,
      text: "Very good experience. The booking process was smooth.",
    },
    {
      name: "Roger P.",
      rating: 5,
      text: "Excellent clinic with professional staff.",
    },
    {
      name: "Diane S.",
      rating: 5,
      text: "Best massage I've ever had. Very satisfied!",
    },
    {
      name: "Gerald W.",
      rating: 4,
      text: "Good service overall. Clean and welcoming.",
    },
    {
      name: "Alice H.",
      rating: 5,
      text: "Professional therapists who understand your needs.",
    },
    {
      name: "Keith B.",
      rating: 5,
      text: "Amazing experience! Will definitely return.",
    },
    {
      name: "Marie D.",
      rating: 4,
      text: "Very good massage therapy. The staff is knowledgeable.",
    },
    {
      name: "Lawrence N.",
      rating: 5,
      text: "Excellent service from start to finish.",
    },
    {
      name: "Gladys F.",
      rating: 5,
      text: "Outstanding therapists and a relaxing atmosphere.",
    },
    {
      name: "Dale G.",
      rating: 4,
      text: "Good experience. Clean and professional environment.",
    },
  ],
];

/**
 * Get rotated reviews for a specific clinic slug
 * Uses hash of clinicSlug to determine which sub-array and rotation offset to use
 */
function getRotatedReviews(clinicSlug) {
  if (!clinicSlug) return REVIEWS_3D[0] || [];

  // Create a simple hash from the clinic slug
  let hash = 0;
  for (let i = 0; i < clinicSlug.length; i++) {
    const char = clinicSlug.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use hash to select sub-array (ensure positive index)
  const subArrayIndex = Math.abs(hash) % REVIEWS_3D.length;
  const selectedSubArray = REVIEWS_3D[subArrayIndex] || REVIEWS_3D[0];

  // Use hash to determine rotation offset
  const rotationOffset = Math.abs(hash) % selectedSubArray.length;

  // Rotate the array
  const rotated = [
    ...selectedSubArray.slice(rotationOffset),
    ...selectedSubArray.slice(0, rotationOffset),
  ];

  // Shuffle the reviews to ensure uniqueness (using hash as seed)
  const shuffled = [...rotated];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.abs(hash + i) % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

/**
 * Check if clinic is currently open based on operating hours
 */
function isClinicOpen(operatingHours) {
  if (!operatingHours || !operatingHours.weekly) {
    return null; // Unknown status
  }

  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const dayKey = dayNames[dayOfWeek];

  const dayHours = operatingHours.weekly[dayKey];

  if (!dayHours || dayHours.closed) {
    return false;
  }

  if (!dayHours.ranges || dayHours.ranges.length === 0) {
    return false;
  }

  // Get current time in 24-hour format
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTime = `${currentHour
    .toString()
    .padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}`;

  // Check if current time falls within any range
  return dayHours.ranges.some((range) => {
    return currentTime >= range.open && currentTime < range.close;
  });
}

/**
 * Format time from 24-hour to 12-hour format
 */
function formatTime(time24) {
  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
}

/**
 * Get formatted operating hours for display
 */
function getFormattedOperatingHours(operatingHours) {
  if (!operatingHours || !operatingHours.weekly) {
    return null;
  }

  const dayNames = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  };

  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  return days.map((dayKey) => {
    const dayHours = operatingHours.weekly[dayKey];
    const dayName = dayNames[dayKey];

    if (!dayHours || dayHours.closed) {
      return { day: dayName, hours: "Closed" };
    }

    if (!dayHours.ranges || dayHours.ranges.length === 0) {
      return { day: dayName, hours: "Closed" };
    }

    const formattedRanges = dayHours.ranges.map((range) => {
      return `${formatTime(range.open)} - ${formatTime(range.close)}`;
    });

    return {
      day: dayName,
      hours: formattedRanges.join(", "),
    };
  });
}

export default function ClinicPage() {
  const { clinicSlug } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(null);

  // Get rotated reviews based on clinic slug
  const reviews = useMemo(() => getRotatedReviews(clinicSlug), [clinicSlug]);

  useEffect(() => {
    const fetchClinic = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          API_ENDPOINTS.seo.getClinic(clinicSlug),
          DEFAULT_FETCH_CONFIG,
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError("Clinic not found");
          } else {
            setError("Failed to load clinic");
          }
          return;
        }

        const data = await response.json();
        setClinic(data);

        // Check if clinic is open
        if (data.operatingHours) {
          setIsOpen(isClinicOpen(data.operatingHours));
        }
      } catch (err) {
        console.error("Error fetching clinic:", err);
        setError("Failed to load clinic");
      } finally {
        setLoading(false);
      }
    };

    if (clinicSlug) {
      fetchClinic();
    }
  }, [clinicSlug]);

  // Update open/closed status every minute
  useEffect(() => {
    if (!clinic?.operatingHours) return;

    const interval = setInterval(() => {
      setIsOpen(isClinicOpen(clinic.operatingHours));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [clinic?.operatingHours]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#98C454] border-t-transparent mx-auto"></div>
          <p className="mt-4 text-[#252F4F] font-medium">Loading clinic...</p>
        </div>
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-white">
        <NavbarDemo />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4 text-[#252F4F]">
            Clinic Not Found
          </h1>
          <p className="text-[#252F4F]/70 mb-8">
            {error || "The clinic you're looking for doesn't exist."}
          </p>
          <Button
            className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] font-semibold"
            onClick={() => navigate("/")}>
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const jsonLd = clinic.jsonLd || {};
  const operatingHours = getFormattedOperatingHours(clinic.operatingHours);
  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        ).toFixed(1)
      : "5.0";

  return (
    <>
      <SEOHead
        title={clinic.seoTitle || clinic.clinicName}
        description={clinic.seoDescription || clinic.description}
        canonical={`https://bookatherapy.com/clinics/${clinic.slug}`}
        ogImage={clinic.heroImage}
        ogType="website"
        schema={Object.keys(jsonLd).length > 0 ? jsonLd : undefined}
      />

      <div className="min-h-screen bg-white">
        <NavbarDemo />
        <ChatBot />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#252F4F] via-[#1e2640] to-[#252F4F] pt-20 pb-12">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-96 h-96 bg-[#98C454]/30 rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
            <div className="absolute top-40 right-10 w-96 h-96 bg-[#98C454]/20 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              {clinic.clinicName}
            </h1>
            {clinic.city?.name && (
              <p className="text-xl text-gray-200">{clinic.city.name}</p>
            )}
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Address & Contact */}
            <div className="lg:col-span-1 space-y-6">
              {/* Clinic Address */}
              <Card className="p-6 shadow-lg border-gray-200">
                <h2 className="text-2xl font-bold mb-4 text-[#252F4F]">
                  Location
                </h2>
                {clinic.address && (
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-[#98C454] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-700 font-medium">
                        {clinic.address}
                      </p>
                      {clinic.city?.name && (
                        <p className="text-gray-600 text-sm mt-1">
                          {clinic.city.name}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </Card>

              {/* Platform Contact Information */}
              <Card className="p-6 shadow-lg border-gray-200">
                <h2 className="text-2xl font-bold mb-4 text-[#252F4F]">
                  Contact Us
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Phone className="h-5 w-5 text-[#98C454] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Phone</p>
                      <a
                        href={`tel:${PLATFORM_CONTACT.phone.replace(
                          /\s/g,
                          "",
                        )}`}
                        className="text-[#252F4F] hover:text-[#98C454] transition-colors font-medium">
                        {PLATFORM_CONTACT.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-[#98C454] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <a
                        href={`mailto:${PLATFORM_CONTACT.email}`}
                        className="text-[#252F4F] hover:text-[#98C454] transition-colors font-medium break-all">
                        {PLATFORM_CONTACT.email}
                      </a>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Operating Hours */}
              {operatingHours && (
                <Card className="p-6 shadow-lg border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-[#252F4F]">Hours</h2>
                    {isOpen !== null && (
                      <div className="flex items-center gap-2">
                        {isOpen ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span className="text-green-600 font-semibold text-sm">
                              Open
                            </span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-5 w-5 text-red-500" />
                            <span className="text-red-600 font-semibold text-sm">
                              Closed
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {operatingHours.map((dayInfo, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="text-gray-700 font-medium">
                          {dayInfo.day}
                        </span>
                        <span className="text-gray-600 text-sm">
                          {dayInfo.hours}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Book Appointment Button */}
              <Button
                className="w-full bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] font-semibold shadow-lg transition-all duration-300 text-lg py-6"
                onClick={() => navigate("/book")}>
                Book Appointment
              </Button>
            </div>

            {/* Right Column - Reviews */}
            <div className="lg:col-span-2">
              <Card className="p-6 md:p-8 shadow-lg border-gray-200">
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                    <span className="text-3xl font-bold text-[#252F4F]">
                      {averageRating}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    <span className="font-semibold">{reviews.length}</span>{" "}
                    reviews
                  </div>
                </div>

                <div className="space-y-8">
                  {reviews.map((review, idx) => (
                    <div
                      key={idx}
                      className="pb-6 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex items-start gap-4 mb-3">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-[#98C454]/20 flex items-center justify-center">
                            <span className="text-[#252F4F] font-semibold text-lg">
                              {review.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <h3 className="font-semibold text-[#252F4F] text-lg mb-1">
                                {review.name}
                              </h3>
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-4 w-4 ${
                                      i < review.rating
                                        ? "text-yellow-400 fill-yellow-400"
                                        : "text-gray-300"
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-700 mt-3 leading-relaxed">
                            {review.text}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
